import { useCallback, useEffect, useState } from 'react';
import { fetchAllowedIndustries } from '../../client/permissionClient';

export type AllowedIndustriesStatus = 'loading' | 'ready' | 'failed';

export interface AllowedIndustriesState {
  /** `null` until the permitted set settles; `[]` on `ready` is a caller permitted nothing. */
  industries: string[] | null;
  status: AllowedIndustriesStatus;
  reload: () => void;
}

/**
 * The caller's OWN permitted industries, for the STO view's industry scope.
 *
 * Lifted out of IndustrySelect because the page now needs the list itself: the STO view
 * queries exactly ONE industry at a time and defaults to the first of these, so the list
 * is request scope, not just dropdown options. Owned by the page, it is fetched once and
 * read by both the selector and the three STO requests, which cannot then disagree about
 * which industries exist.
 *
 * GetAllowedIndustries and never ListIndustries, so a user is never offered — nor queried
 * for — an industry they are not allowed to read. The order is the service's, taken
 * verbatim (see permissionClient), which is what makes "the first one" well defined.
 *
 * ⚠️ A failed fetch is fail-CLOSED now, and that is a reversal. It used to leave the
 * selector on an ALL option that sent no `industries` at all and let the service scope the
 * answer, so losing the list cost only the ability to narrow. With no ALL there is no
 * request that can be sent without an industry picked from this list, so `failed` has to
 * stop the view rather than fall back to one.
 *
 * `enabled` keeps the GM view off the wire: that view has no selector, and an ADMIN_ONLY
 * page should not be resolving scope for a control it never shows. The settled answer is
 * kept across a disable, so a trip through GM does not refetch or re-default the STO view.
 */
export function useAllowedIndustries(sub: string, enabled: boolean): AllowedIndustriesState {
  /** Tagged with the caller it was fetched for, so a different `sub` can never read it. */
  const [settled, setSettled] = useState<{ sub: string; industries: string[] } | null>(null);
  const [failed, setFailed] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  const settledSub = settled?.sub ?? null;

  useEffect(() => {
    // Only a successful answer is final; a failed one is retried through `reload`.
    if (!enabled || settledSub === sub) return;
    let cancelled = false;
    setFailed(false);
    (async () => {
      const result = await fetchAllowedIndustries(sub);
      if (cancelled) return;
      if (result.ok) setSettled({ sub, industries: result.industries });
      else setFailed(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [sub, enabled, reloadKey, settledSub]);

  const reload = useCallback(() => setReloadKey((k) => k + 1), []);

  const industries = settled !== null && settled.sub === sub ? settled.industries : null;
  const status: AllowedIndustriesStatus =
    industries !== null ? 'ready' : failed ? 'failed' : 'loading';

  return { industries, status, reload };
}
